import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  DatePicker,
  Select,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Modal,
  InputNumber,
} from 'antd';
import {
  ReloadOutlined,
  FileExcelOutlined,
  EyeOutlined,
  CalendarOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { buildAPIURL } from '../../config/apiConfig';
import * as XLSX from 'xlsx';

const { RangePicker } = DatePicker;

interface MeetingSession {
  SessionID: number;
  MeetingTitle: string;
  MeetingDescription?: string;
  MeetingDate: string;
  MeetingSession: string;
  StartTime: string;
  Location?: string;
  Status: string;
  TotalAttendees: number;
  CreatedBy: string;
  CreatedAt: string;
  ClosedAt?: string;
}

interface Attendee {
  STT: number;
  Username: string;
  FullName: string;
  RoleName: string;
  CheckInTime: string;
}

interface SuspiciousDeviceGroup {
  SessionID: number;
  MeetingTitle: string;
  MeetingDate: string;
  MeetingSession: string;
  StartTime: string;
  DeviceGroupKey: string;
  UserAgent: string;
  SourceIP: string;
  DeviceFingerprint: string;
  DeviceInfoJson?: string | null;
  attendeeCount: number;
  distinctUsers: number;
  distinctUserAgents: number;
  distinctIPs: number;
  usernames: string;
  fullNames: string;
  allUserAgents?: string;
  allIPs?: string;
  checkInDurationSeconds?: number;
  fraudScore: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  firstCheckIn: string;
  lastCheckIn: string;
}

const MeetingStatisticsPage: React.FC = () => {
  const [meetings, setMeetings] = useState<MeetingSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingSession | null>(null);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [suspiciousDevices, setSuspiciousDevices] = useState<SuspiciousDeviceGroup[]>([]);
  const [suspiciousLoading, setSuspiciousLoading] = useState(false);
  const [suspiciousMinUsers, setSuspiciousMinUsers] = useState<number>(3);

  // Helper function to get clean token from localStorage
  const getAuthToken = useCallback((): string => {
    // Try both possible token keys
    let token = localStorage.getItem('quanly_truonghoc_token') || localStorage.getItem('token');
    
    if (!token) {
      console.error('❌ No token found in localStorage');
      throw new Error('Chưa đăng nhập. Vui lòng đăng nhập lại.');
    }
    
    // Remove surrounding quotes if present
    if (token.startsWith('"') && token.endsWith('"')) {
      token = token.slice(1, -1);
    }
    
    return token;
  }, []);

  const fetchSuspiciousDevices = useCallback(async (sessionId: number, minUsers: number) => {
    try {
      setSuspiciousLoading(true);
      const token = getAuthToken();
      const url = buildAPIURL(`/meeting-attendance/${sessionId}/suspicious-devices?minUsers=${minUsers}`);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Không thể tải thiết bị nghi vấn');
      }

      const data = await response.json();
      if (data.success) {
        setSuspiciousDevices(data.data || []);
      } else {
        setSuspiciousDevices([]);
      }
    } catch (err: any) {
      setSuspiciousDevices([]);
      message.error(err.message || 'Lỗi tải thiết bị nghi vấn');
    } finally {
      setSuspiciousLoading(false);
    }
  }, [getAuthToken]);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();

      let url = buildAPIURL('/meeting-attendance');
      const params = new URLSearchParams();
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.append('fromDate', dateRange[0].format('YYYY-MM-DD'));
        params.append('toDate', dateRange[1].format('YYYY-MM-DD'));
      }
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Không thể tải dữ liệu');

      const data = await response.json();
      if (data.success) {
        setMeetings(data.data || []);
      }
    } catch (err: any) {
      message.error(err.message || 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeetings();
  }, []);

  const viewDetail = async (meeting: MeetingSession) => {
    try {
      setSelectedMeeting(meeting);
      setDetailModalOpen(true);
      setSuspiciousDevices([]);

      const token = getAuthToken();
      const response = await fetch(buildAPIURL(`/meeting-attendance/${meeting.SessionID}/attendees`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Không thể tải danh sách tham dự');

      const data = await response.json();
      if (data.success) {
        setAttendees(data.data || []);
      }
    } catch (err: any) {
      message.error(err.message || 'Lỗi tải chi tiết');
    }
  };

  useEffect(() => {
    if (detailModalOpen && selectedMeeting) {
      fetchSuspiciousDevices(selectedMeeting.SessionID, suspiciousMinUsers);
    }
  }, [detailModalOpen, selectedMeeting, suspiciousMinUsers, fetchSuspiciousDevices]);

  const exportToExcel = (meeting: MeetingSession, attendeeList: Attendee[]) => {
    try {
      const worksheetData = [
        ['BÁO CÁO ĐIỂM DANH CUỘC HỌP'],
        [],
        ['Tiêu đề:', meeting.MeetingTitle],
        ['Mô tả:', meeting.MeetingDescription || ''],
        ['Ngày:', dayjs(meeting.MeetingDate).format('DD/MM/YYYY')],
        ['Buổi:', meeting.MeetingSession],
        ['Giờ bắt đầu:', meeting.StartTime],
        ['Địa điểm:', meeting.Location || ''],
        ['Người tạo:', meeting.CreatedBy],
        ['Tổng số người tham dự:', meeting.TotalAttendees],
        [],
        ['STT', 'Họ và tên', 'Username', 'Vai trò', 'Thời gian điểm danh'],
        ...attendeeList.map(a => {
          // Remove timezone indicator and parse as local Vietnam time
          let cleanTime = a.CheckInTime;
          cleanTime = cleanTime.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
          cleanTime = cleanTime.replace(/\.\d{3}/, '');
          const parsed = dayjs(cleanTime);
          
          return [
            a.STT,
            a.FullName,
            a.Username,
            a.RoleName,
            parsed.format('DD/MM/YYYY HH:mm:ss'),
          ];
        }),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Điểm danh');

      // Auto-width columns
      const maxWidth = worksheetData.reduce((w, r) => Math.max(w, r.length), 0);
      worksheet['!cols'] = Array(maxWidth).fill({ wch: 20 });

      const fileName = `DiemDanhCuocHop_${meeting.SessionID}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      message.success('Đã xuất file Excel');
    } catch (err: any) {
      message.error('Lỗi xuất Excel: ' + (err.message || ''));
    }
  };

  const exportAllToExcel = () => {
    try {
      const worksheetData = [
        ['BÁO CÁO TỔNG HỢP CUỘC HỌP'],
        [],
        ['Từ ngày:', dateRange[0].format('DD/MM/YYYY')],
        ['Đến ngày:', dateRange[1].format('DD/MM/YYYY')],
        [],
        ['STT', 'Tiêu đề', 'Ngày', 'Buổi', 'Giờ', 'Địa điểm', 'Trạng thái', 'Số người tham dự', 'Người tạo'],
        ...meetings.map((m, idx) => [
          idx + 1,
          m.MeetingTitle,
          dayjs(m.MeetingDate).format('DD/MM/YYYY'),
          m.MeetingSession,
          m.StartTime,
          m.Location || '',
          m.Status === 'open' ? 'Đang mở' : 'Đã đóng',
          m.TotalAttendees,
          m.CreatedBy,
        ]),
        [],
        ['Tổng số cuộc họp:', meetings.length],
        ['Tổng số người tham dự:', meetings.reduce((sum, m) => sum + m.TotalAttendees, 0)],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tổng hợp');

      worksheet['!cols'] = Array(9).fill({ wch: 20 });

      const fileName = `TongHopCuocHop_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      message.success('Đã xuất file Excel');
    } catch (err: any) {
      message.error('Lỗi xuất Excel: ' + (err.message || ''));
    }
  };

  const columns = [
    {
      title: 'STT',
      key: 'index',
      render: (_: any, __: any, index: number) => index + 1,
      width: 60,
    },
    {
      title: 'Tiêu đề cuộc họp',
      dataIndex: 'MeetingTitle',
      key: 'MeetingTitle',
      width: 250,
    },
    {
      title: 'Ngày',
      dataIndex: 'MeetingDate',
      key: 'MeetingDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      width: 100,
    },
    {
      title: 'Buổi',
      dataIndex: 'MeetingSession',
      key: 'MeetingSession',
      width: 80,
    },
    {
      title: 'Giờ',
      dataIndex: 'StartTime',
      key: 'StartTime',
      render: (time: string) => {
        if (!time) return '-';
        if (time.includes('T') || time.includes('Z')) {
          return dayjs(time).format('HH:mm');
        }
        return time.substring(0, 5);
      },
      width: 80,
    },
    {
      title: 'Địa điểm',
      dataIndex: 'Location',
      key: 'Location',
      width: 150,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      render: (status: string) => (
        <Tag color={status === 'open' ? 'green' : 'default'}>
          {status === 'open' ? 'Đang mở' : 'Đã đóng'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Số người tham dự',
      dataIndex: 'TotalAttendees',
      key: 'TotalAttendees',
      align: 'center' as const,
      width: 120,
      render: (count: number) => <strong style={{ color: '#1890ff' }}>{count}</strong>,
    },
    {
      title: 'Người tạo',
      dataIndex: 'CreatedBy',
      key: 'CreatedBy',
      width: 120,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: MeetingSession) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => viewDetail(record)}
          size="small"
        >
          Xem chi tiết
        </Button>
      ),
      width: 120,
    },
  ];

  const attendeeColumns = [
    { title: 'STT', dataIndex: 'STT', key: 'STT', width: 60 },
    { title: 'Họ và tên', dataIndex: 'FullName', key: 'FullName' },
    { title: 'Username', dataIndex: 'Username', key: 'Username', width: 150 },
    { title: 'Vai trò', dataIndex: 'RoleName', key: 'RoleName', width: 120 },
    {
      title: 'Thời gian điểm danh',
      dataIndex: 'CheckInTime',
      key: 'CheckInTime',
      render: (time: string) => {
        if (!time) return '-';
        
        // Remove timezone indicator and parse as local Vietnam time
        // Backend đã lưu theo giờ Việt Nam (UTC+7)
        let cleanTime = time;
        
        // Remove 'Z' or timezone offset if present
        cleanTime = cleanTime.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
        
        // Remove milliseconds if present (2025-10-08T10:09:14.349Z -> 2025-10-08T10:09:14)
        cleanTime = cleanTime.replace(/\.\d{3}/, '');
        
        // Parse as local time (không tự động convert timezone)
        const parsed = dayjs(cleanTime);
        
        return parsed.format('DD/MM/YYYY HH:mm:ss');
      },
      width: 180,
    },
  ];

  const suspiciousColumns = [
    {
      title: 'Mức độ nghi vấn',
      key: 'fraudScore',
      width: 140,
      render: (record: SuspiciousDeviceGroup) => {
        const { fraudScore, riskLevel } = record;
        let color = 'green';
        let text = 'Thấp';
        
        if (riskLevel === 'HIGH') {
          color = 'red';
          text = 'Cao';
        } else if (riskLevel === 'MEDIUM') {
          color = 'orange';
          text = 'Trung bình';
        }
        
        return (
          <Space direction="vertical" size={2}>
            <Tag color={color} style={{ marginBottom: 0, fontWeight: 'bold' }}>{text}</Tag>
            <span style={{ color: '#888', fontSize: 12 }}>{fraudScore} điểm</span>
          </Space>
        );
      },
      sorter: (a: SuspiciousDeviceGroup, b: SuspiciousDeviceGroup) => b.fraudScore - a.fraudScore,
    },
    {
      title: 'Fingerprint / Thiết bị nghi vấn',
      key: 'DeviceFingerprint',
      width: 360,
      render: (record: SuspiciousDeviceGroup) => {
        const fingerprint = record.DeviceFingerprint && record.DeviceFingerprint !== 'UNKNOWN'
          ? record.DeviceFingerprint
          : 'Không xác định';
        const userAgent = record.UserAgent && record.UserAgent !== 'UNKNOWN'
          ? record.UserAgent
          : 'Không xác định';
        const fullNames = record.fullNames?.split(', ') || [];
        const usernames = record.usernames?.split(', ') || [];
        const accounts = usernames
          .map((username, idx) => ({
            username: username?.trim() || '',
            fullName: fullNames[idx]?.trim() || '',
          }))
          .filter(item => item.username.length > 0);

        return (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space direction="vertical" size={0}>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{fingerprint}</span>
              <span style={{ color: '#888', fontSize: 12 }}>{record.DeviceGroupKey}</span>
            </Space>
            <span style={{ color: '#555', fontSize: 12 }}>Thiết bị: {userAgent}</span>
            <div>
              {accounts.length === 0 ? (
                <span style={{ color: '#999', fontSize: 12 }}>Không có dữ liệu tài khoản</span>
              ) : (
                accounts.map((account, idx) => (
                  <div
                    key={`${record.DeviceGroupKey}-${account.username || 'unknown'}-${idx}`}
                    style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}
                  >
                    <Tag color="blue" style={{ marginBottom: 0 }}>{account.username}</Tag>
                    <span style={{ color: '#666' }}>{account.fullName || 'Không rõ họ tên'}</span>
                  </div>
                ))
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Số tài khoản / IP',
      key: 'userStats',
      width: 140,
      render: (record: SuspiciousDeviceGroup) => (
        <Space direction="vertical" size={2}>
          <span>{record.distinctUsers} users</span>
          <span style={{ color: '#888', fontSize: 12 }}>{record.distinctIPs} IPs</span>
        </Space>
      ),
    },
    {
      title: 'Thời gian điểm danh',
      key: 'checkinWindow',
      width: 240,
      render: (record: SuspiciousDeviceGroup) => {
        const duration = record.checkInDurationSeconds || 0;
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        
        return (
          <Space direction="vertical" size={2}>
            <div>Từ: {dayjs(record.firstCheckIn).format('HH:mm:ss')}</div>
            <div>Đến: {dayjs(record.lastCheckIn).format('HH:mm:ss')}</div>
            <span style={{ color: '#888', fontSize: 12 }}>
              Khoảng: {minutes}m {seconds}s ({duration}s)
            </span>
          </Space>
        );
      },
    },
  ];

  const totalMeetings = meetings.length;
  const totalAttendees = meetings.reduce((sum, m) => sum + m.TotalAttendees, 0);
  const openMeetings = meetings.filter(m => m.Status === 'open').length;

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>Thống kê cuộc họp</h2>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Tổng số cuộc họp"
              value={totalMeetings}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Tổng số người tham dự"
              value={totalAttendees}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Cuộc họp đang diễn ra"
              value={openMeetings}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
            format="DD/MM/YYYY"
          />
          <Select
            style={{ width: 150 }}
            placeholder="Trạng thái"
            allowClear
            value={statusFilter || undefined}
            onChange={(value) => setStatusFilter(value || '')}
            options={[
              { label: 'Tất cả', value: '' },
              { label: 'Đang mở', value: 'open' },
              { label: 'Đã đóng', value: 'closed' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={loadMeetings}>
            Tải lại
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            type="primary"
            onClick={exportAllToExcel}
            disabled={meetings.length === 0}
          >
            Xuất Excel
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card title="Danh sách cuộc họp">
        <Table
          columns={columns}
          dataSource={meetings}
          rowKey="SessionID"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={`Chi tiết cuộc họp: ${selectedMeeting?.MeetingTitle || ''}`}
        open={detailModalOpen}
        onCancel={() => {
          setDetailModalOpen(false);
          setSelectedMeeting(null);
          setSuspiciousDevices([]);
        }}
        width={900}
        footer={[
          <Button
            key="export"
            icon={<FileExcelOutlined />}
            onClick={() => {
              if (selectedMeeting) exportToExcel(selectedMeeting, attendees);
            }}
          >
            Xuất Excel
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setDetailModalOpen(false);
              setSelectedMeeting(null);
              setSuspiciousDevices([]);
            }}
          >
            Đóng
          </Button>,
        ]}
      >
        {selectedMeeting && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div><strong>Ngày:</strong> {dayjs(selectedMeeting.MeetingDate).format('DD/MM/YYYY')}</div>
                  <div><strong>Buổi:</strong> {selectedMeeting.MeetingSession}</div>
                  <div><strong>Giờ bắt đầu:</strong> {selectedMeeting.StartTime}</div>
                </Col>
                <Col span={12}>
                  <div><strong>Địa điểm:</strong> {selectedMeeting.Location || 'Không có'}</div>
                  <div><strong>Người tạo:</strong> {selectedMeeting.CreatedBy}</div>
                  <div><strong>Số người tham dự:</strong> <span style={{ color: '#1890ff', fontSize: 16, fontWeight: 'bold' }}>{selectedMeeting.TotalAttendees}</span></div>
                </Col>
              </Row>
              {selectedMeeting.MeetingDescription && (
                <div style={{ marginTop: 12 }}>
                  <strong>Mô tả:</strong>
                  <div style={{ padding: '8px 12px', background: '#f0f2f5', borderRadius: 4, marginTop: 4 }}>
                    {selectedMeeting.MeetingDescription}
                  </div>
                </div>
              )}
            </Card>

            <h4>Danh sách người tham dự</h4>
            <Table
              columns={attendeeColumns}
              dataSource={attendees}
              rowKey={(record) => `${record.STT}-${record.Username}`}
              pagination={false}
              scroll={{ y: 300 }}
              size="small"
            />

            <div style={{ marginTop: 24 }}>
              <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                <Col>
                  <h4 style={{ marginBottom: 0 }}>Thiết bị nghi vấn</h4>
                  <div style={{ color: '#888' }}>Các thiết bị có nhiều tài khoản điểm danh trong phiên</div>
                </Col>
                <Col>
                  <Space>
                    <span>Tối thiểu số tài khoản:</span>
                    <InputNumber
                      min={2}
                      max={20}
                      value={suspiciousMinUsers}
                      onChange={(value) => {
                        if (typeof value === 'number' && value >= 2) {
                          setSuspiciousMinUsers(value);
                        }
                      }}
                    />
                    {selectedMeeting && (
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetchSuspiciousDevices(selectedMeeting.SessionID, suspiciousMinUsers)}
                        size="small"
                      >
                        Làm mới
                      </Button>
                    )}
                  </Space>
                </Col>
              </Row>
              <Table
                columns={suspiciousColumns}
                dataSource={suspiciousDevices}
                rowKey={(record) => `${record.UserAgent}-${record.SourceIP}-${record.firstCheckIn}`}
                pagination={false}
                loading={suspiciousLoading}
                locale={{ emptyText: 'Không phát hiện thiết bị nghi vấn' }}
                size="small"
                scroll={{ y: 240 }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MeetingStatisticsPage;
